/**
 * The UMA value form: when it is complete enough to send, and what the server's 409 asks for.
 * The server decides everything else (who may enter, the source rules, the 10% line).
 */
import axios from 'axios';
import type { LargeChange, UmaEntry } from '../services/umaValues.service';

export const LARGE_CHANGE_CODE = 'uma_large_change_needs_confirmation';

/** Pesos with at most two decimals; the daily value, not the monthly or annual one. */
const MONEY = /^\d{1,10}(\.\d{1,2})?$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

export interface UmaForm {
  value: string;
  effectiveFrom: string;
  dofPublicationDate: string;
  appliesToYear: string;
}

export function isMoney(value: string): boolean {
  return MONEY.test(value.trim()) && Number(value) > 0;
}

export function canSend(form: UmaForm): boolean {
  return isMoney(form.value)
    && DAY.test(form.effectiveFrom)
    && DAY.test(form.dofPublicationDate)
    && (form.appliesToYear.trim() === '' || /^\d{4}$/.test(form.appliesToYear.trim()));
}

export function toEntry(form: UmaForm, confirmLargeChange: boolean): UmaEntry {
  const year = form.appliesToYear.trim();
  return {
    daily_value_mxn: form.value.trim(),
    effective_from: form.effectiveFrom,
    dof_publication_date: form.dofPublicationDate,
    applies_to_year: year === '' ? null : Number(year),
    confirm_large_change: confirmLargeChange,
  };
}

/** The 409 that asks for the explicit confirmation, or null for any other error. */
export function largeChangeOf(err: unknown): LargeChange | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return null;
  const detail = err.response.data?.detail;
  if (detail?.code !== LARGE_CHANGE_CODE) return null;
  return {
    current_value: String(detail.current_value),
    difference_percent: String(detail.difference_percent),
  };
}

/** The value as the screen shows it: pesos and the unit it is a value of. */
export function pesos(value: string): string {
  return `$${value} MXN`;
}
